import { ChangeDetectionStrategy, Component, Inject, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReviewerDetails } from '@app/app/page-modules/playlist/models/reviews-settings.model';
import { LEARNER_PLAYLIST_DATA_SERVICE, LearnerPlaylistDataService } from '@app/app/shared/services/learner-playlist/data.service';
import { AutocompleteSearchOption } from '@app/app/shared/styleguide/components/autocomplete-search/sg-autocomplete-search.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

export interface V3SubmitReviewDialogData {
  playlistUid: string;
  playlistTitle: string;
  userReviewSummary?: {
    reviewType?: string;
    maxSelectableReviewers?: number;
    areReviewersFixed?: boolean;
    reviewers?: ReviewerDetails[];
  };
}

export interface V3SubmitReviewResult {
  reviewers: string[];
  commentToReviewers: string;
}

@Component({
  selector: 'ptl-v3-submit-review-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule, StyleguideModule],
  templateUrl: './v3-submit-review-dialog.component.html',
  styleUrl: './v3-submit-review-dialog.component.scss',
})
export class V3SubmitReviewDialogComponent {
  protected note = signal('');
  protected selectedReviewerUids = signal<string[]>([]);
  protected showReviewerSelect = false;
  protected maxReviewers: number | null = null;

  private knownReviewers = signal<ReviewerDetails[]>([]);
  private searchResults = signal<ReviewerDetails[]>([]);
  private reviewerSearch$ = new Subject<string>();

  protected reviewerOptions = computed<AutocompleteSearchOption<string>[]>(() => {
    const byUid = new Map<string, ReviewerDetails>();
    for (const r of this.knownReviewers()) byUid.set(r.uid, r);
    for (const r of this.searchResults()) byUid.set(r.uid, r);
    return Array.from(byUid.values()).map((r) => ({
      value: r.uid,
      label: `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email,
    }));
  });

  protected canSubmit = computed(() => !this.showReviewerSelect || this.selectedReviewerUids().length > 0);

  constructor(
    private dialogRef: MatDialogRef<V3SubmitReviewDialogComponent, V3SubmitReviewResult>,
    @Inject(MAT_DIALOG_DATA) protected data: V3SubmitReviewDialogData,
    @Inject(LEARNER_PLAYLIST_DATA_SERVICE) private learnerPlaylistDataService: LearnerPlaylistDataService,
  ) {
    const summary = this.data.userReviewSummary;
    // Mirror v2 (playlist-action-bar): the learner picks reviewers whenever the review is not automatic and
    // reviewers are not fixed (a pool). maxSelectableReviewers is only a cap, never a gate (null = no limit).
    this.showReviewerSelect = !!summary && summary.reviewType !== 'AUTOMATIC' && !summary.areReviewersFixed;
    this.maxReviewers = summary?.maxSelectableReviewers ?? null;

    const active = (summary?.reviewers ?? []).filter((r) => !r.rejected);
    this.knownReviewers.set(active);
    const activeUids = active.map((r) => r.uid);
    this.selectedReviewerUids.set(this.maxReviewers ? activeUids.slice(0, this.maxReviewers) : activeUids);

    this.reviewerSearch$
      .pipe(
        debounceTime(300),
        switchMap((term) => {
          const uid = this.data.playlistUid;
          if (!uid) return of(null);
          return this.learnerPlaylistDataService.loadPlaylistReviewers(uid, 0, 20, term).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(),
      )
      .subscribe((res) => {
        if (res?.isSuccess && res.value) {
          this.searchResults.set(res.value.content ?? []);
        }
      });
  }

  protected onReviewerSearch(term: string): void {
    this.reviewerSearch$.next(term);
  }

  protected onReviewersChange(value: string | string[] | null): void {
    let arr = Array.isArray(value) ? value : value ? [value] : [];
    if (this.maxReviewers && arr.length > this.maxReviewers) {
      arr = arr.slice(0, this.maxReviewers);
    }
    this.selectedReviewerUids.set(arr);
  }

  protected onNoteChange(value: string): void {
    this.note.set(value);
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected submit(): void {
    if (!this.canSubmit()) return;
    this.dialogRef.close({
      reviewers: this.selectedReviewerUids(),
      commentToReviewers: this.note().trim(),
    });
  }
}
